<script lang="ts">
import type { SelectContentProps } from 'reka-ui'

import type { SelectProps } from './types'
</script>

<script setup lang="ts" generic="T extends string | number | boolean | Record<string, any>, V extends string">
import { capitalize } from '@docue/utils/string'

const props = withDefaults(defineProps<SelectProps<T, V> & { selectContent?: SelectContentProps }>(), {
  size: 'md',
  getItemText: (option: T) => option.toString(),
})

const emits = defineEmits<{
  'update:open': [isOpen: boolean]
}>()

const { t: tg } = useI18n({ useScope: 'global' })

const selected = defineModel<V>()

const placeholder = computed(() => props.placeholder ?? capitalize(tg('selectAnOption')))

const displayedPlaceholder = computed(() => {
  if (selected.value) {
    const selectedOpt = props.options.find(option => props.getItemValue(option) === selected.value)
    return selectedOpt ? props.getItemText(selectedOpt) : ''
  }
  return placeholder.value
})
</script>

<template>
  <UiSelect
    v-model="selected"
    :has-error
    @update:open="emits('update:open', $event)"
  >
    <slot
      name="trigger"
      :size
    >
      <UiSelectTrigger
        :id="props.id"
        :size
        :class="props.classTrigger"
      >
        <slot
          name="trigger-content"
          :displayed-placeholder
          :selected
        >
          <UiSelectValue :placeholder>
            <template #default>
              {{ displayedPlaceholder }}
            </template>
          </UiSelectValue>
        </slot>
      </UiSelectTrigger>
    </slot>

    <UiSelectContent
      :style="fitWidthToTrigger ? 'max-width: var(--reka-popper-anchor-width)' : ''"
      v-bind="selectContent"
    >
      <UiSelectGroup>
        <UiSelectLabel v-if="selectLabel">
          {{ selectLabel }}
        </UiSelectLabel>

        <UiSelectItem
          v-for="option in options"
          :key="getItemValue(option)"
          :value="getItemValue(option).toString()"
        >
          <template #content>
            <slot
              :option
              name="select-item-content"
            />
          </template>
          <template #default>
            <slot
              :option
              :label="getItemText(option)"
            >
              {{ getItemText(option) || '&nbsp;' /* corrects height for empty label options */ }}
            </slot>
          </template>
          <template #description>
            <UiSelectItemDescription :value="getItemValue(option)">
              <slot
                name="description"
                :option
              >
                {{ getItemDescription?.(option) }}
              </slot>
            </UiSelectItemDescription>
          </template>
        </UiSelectItem>
      </UiSelectGroup>
    </UiSelectContent>
  </UiSelect>
</template>
